import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Inbox, Search, ShieldAlert, Clock, AlertOctagon, CheckCircle2, UserCog } from 'lucide-react';
import { ReassignLeadDialog, AgentOption } from './ReassignLeadDialog';

type SlaBucket = 'all' | 'responded' | 'in_window' | 'past_soft' | 'past_24h';

function bucketOf(lead: any, slaMin: number): SlaBucket {
  if (lead.claimed_at || ['contacted', 'converted', 'closed'].includes(lead.status)) return 'responded';
  const ageMs = Date.now() - new Date(lead.created_at).getTime();
  if (ageMs >= 24 * 3600 * 1000) return 'past_24h';
  if (ageMs >= slaMin * 60 * 1000) return 'past_soft';
  return 'in_window';
}

const BUCKET_LABEL: Record<SlaBucket, { label: string; cls: string; icon: any }> = {
  all: { label: 'All', cls: 'bg-muted text-muted-foreground', icon: Inbox },
  responded: { label: 'Responded', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400', icon: CheckCircle2 },
  in_window: { label: 'In SLA', cls: 'bg-amber-500/10 text-amber-600', icon: Clock },
  past_soft: { label: 'Past SLA', cls: 'bg-destructive/10 text-destructive', icon: Clock },
  past_24h: { label: 'Awaiting Reassignment', cls: 'bg-foreground text-background', icon: AlertOctagon },
};

export function AgencyLeadInbox() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBucket = (searchParams.get('sla') as SlaBucket) || 'all';
  const [bucket, setBucket] = useState<SlaBucket>(initialBucket);
  const [search, setSearch] = useState('');
  const [agentFilter, setAgentFilter] = useState<string>('all');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<any | null>(null);

  // Agency
  const { data: agency } = useQuery({
    queryKey: ['agency-inbox-agency', user?.id],
    queryFn: async () => {
      const { data } = await supabase.from('agencies').select('id, name').eq('created_by', user!.id).maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  // Settings (soft SLA)
  const { data: settings } = useQuery({
    queryKey: ['agency-inbox-settings', agency?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from('agency_settings')
        .select('sla_response_minutes')
        .eq('agency_id', agency!.id)
        .maybeSingle();
      return data || { sla_response_minutes: 30 };
    },
    enabled: !!agency,
  });

  // Agents in agency + their permissions + names
  const { data: agents = [] } = useQuery<AgentOption[]>({
    queryKey: ['agency-inbox-agents', agency?.id],
    queryFn: async () => {
      const { data: ap } = await supabase.from('agent_profiles').select('user_id').eq('agency_id', agency!.id);
      const ids = (ap || []).map(a => a.user_id);
      if (!ids.length) return [];
      const [{ data: profs }, { data: perms }] = await Promise.all([
        supabase.from('profiles').select('user_id, full_name').in('user_id', ids),
        supabase.from('agent_permissions').select('agent_user_id, can_take_leads').in('agent_user_id', ids),
      ]);
      const nameMap = Object.fromEntries((profs || []).map(p => [p.user_id, p.full_name || 'Agent']));
      const permMap = Object.fromEntries((perms || []).map(p => [p.agent_user_id, p.can_take_leads]));
      return ids.map(id => ({
        user_id: id,
        full_name: nameMap[id] || 'Agent',
        can_take_leads: permMap[id] !== false,
      }));
    },
    enabled: !!agency,
  });

  // All leads on team listings (RLS + filter)
  const { data: leads = [], isLoading } = useQuery({
    queryKey: ['agency-inbox-leads', agency?.id, agents.length],
    queryFn: async () => {
      const ids = agents.map(a => a.user_id);
      if (!ids.length) return [];
      const { data } = await supabase
        .from('leads')
        .select('id, name, phone, email, message, status, created_at, claimed_at, assigned_to, agent_id, property_id, plot_id, project_id, properties(title, city), plots(title, city), projects(name, city)')
        .or(`agent_id.in.(${ids.join(',')}),assigned_to.in.(${ids.join(',')})`)
        .order('created_at', { ascending: false })
        .limit(500);
      return data || [];
    },
    enabled: !!agency && agents.length > 0,
    refetchInterval: 30000,
  });

  const slaMin = settings?.sla_response_minutes ?? 30;
  const agentNameMap = useMemo(() => Object.fromEntries(agents.map(a => [a.user_id, a.full_name])), [agents]);

  const counts = useMemo(() => {
    const c: Record<SlaBucket, number> = { all: leads.length, responded: 0, in_window: 0, past_soft: 0, past_24h: 0 };
    leads.forEach(l => { c[bucketOf(l, slaMin)]++; });
    return c;
  }, [leads, slaMin]);

  const filtered = useMemo(() => {
    return leads.filter(l => {
      if (bucket !== 'all' && bucketOf(l, slaMin) !== bucket) return false;
      if (agentFilter !== 'all') {
        const ownerId = l.assigned_to || l.agent_id;
        if (ownerId !== agentFilter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = `${l.name || ''} ${l.phone || ''} ${l.email || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [leads, bucket, agentFilter, search, slaMin]);

  const setBucketWithUrl = (b: SlaBucket) => {
    setBucket(b);
    const sp = new URLSearchParams(searchParams);
    if (b === 'all') sp.delete('sla'); else sp.set('sla', b);
    setSearchParams(sp, { replace: true });
  };

  const openReassign = (lead: any) => {
    setSelectedLead(lead);
    setReassignOpen(true);
  };

  if (!user) return null;
  if (!agency) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="p-8 text-center">
          <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
          <p className="text-sm text-muted-foreground">Only agency admins can access the team inbox.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Inbox className="h-5 w-5 text-primary" /> Agency Lead Inbox
          </h2>
          <p className="text-sm text-muted-foreground">
            All inquiries on every <strong>{agency.name}</strong> listing. Leads not contacted within 24h can be reassigned.
          </p>
        </div>
      </div>

      {/* SLA Tabs */}
      <Tabs value={bucket} onValueChange={(v) => setBucketWithUrl(v as SlaBucket)}>
        <TabsList className="flex-wrap h-auto gap-1 bg-muted/50 p-1 rounded-xl">
          {(['all', 'past_24h', 'past_soft', 'in_window', 'responded'] as SlaBucket[]).map(b => {
            const meta = BUCKET_LABEL[b];
            const Icon = meta.icon;
            return (
              <TabsTrigger key={b} value={b} className="gap-1.5 text-xs">
                <Icon className="h-3.5 w-3.5" />
                {meta.label}
                <span className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-background px-1.5 text-[10px] font-semibold">
                  {counts[b]}
                </span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search name, phone, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="All agents" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All agents</SelectItem>
            {agents.map(a => (
              <SelectItem key={a.user_id} value={a.user_id}>{a.full_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="rounded-2xl">
          <CardContent className="p-12 text-center">
            <Inbox className="h-10 w-10 mx-auto text-muted-foreground/40 mb-2" />
            <p className="text-sm text-muted-foreground">No leads match the current filter.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((lead: any) => {
            const b = bucketOf(lead, slaMin);
            const meta = BUCKET_LABEL[b];
            const ownerId = lead.assigned_to || lead.agent_id;
            const ownerName = ownerId ? agentNameMap[ownerId] || 'Agent' : 'Unassigned';
            const ageMs = Date.now() - new Date(lead.created_at).getTime();
            const ageHrs = Math.floor(ageMs / 3600000);
            const listingTitle =
              (lead.properties?.title?.en || lead.properties?.title) ||
              (lead.plots?.title?.en || lead.plots?.title) ||
              lead.projects?.name || '—';
            return (
              <Card key={lead.id} className="rounded-2xl border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{lead.name}</span>
                      <Badge className={`${meta.cls} text-[10px] gap-1`} variant="secondary">
                        <meta.icon className="h-3 w-3" /> {meta.label}
                      </Badge>
                      {b === 'past_24h' && (
                        <span className="text-[10px] text-muted-foreground">{ageHrs}h overdue</span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">{lead.phone} {lead.email ? `· ${lead.email}` : ''}</div>
                    <div className="text-[11px] text-muted-foreground truncate mt-0.5">on “{listingTitle}”</div>
                  </div>
                  <div className="text-xs text-muted-foreground min-w-[140px]">
                    <div className="text-[10px] uppercase tracking-wide">Assigned to</div>
                    <div className="font-medium text-foreground">{ownerName}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {new Date(lead.created_at).toLocaleString()}
                  </div>
                  {b === 'past_24h' && (
                    <Button size="sm" variant="default" className="gap-1.5" onClick={() => openReassign(lead)}>
                      <UserCog className="h-3.5 w-3.5" /> Reassign
                    </Button>
                  )}
                  {b !== 'past_24h' && (
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={() => openReassign(lead)}>
                      <UserCog className="h-3.5 w-3.5" /> Reassign
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <ReassignLeadDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        leadId={selectedLead?.id || null}
        customerName={selectedLead?.name}
        currentAgentId={selectedLead?.assigned_to || selectedLead?.agent_id}
        currentAgentName={selectedLead ? agentNameMap[selectedLead.assigned_to || selectedLead.agent_id] : undefined}
        agents={agents}
        onSuccess={() => qc.invalidateQueries({ queryKey: ['agency-inbox-leads'] })}
      />
    </div>
  );
}
